import { Box, Button, Icon, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiPlus } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { MarketDialogueReach, useListMarketDialoguesQuery } from "../../autogen/bff-api";
import { ListPageHeader } from "../../common/components/ListPageHeader";
import { ChooseEventDepartmentModal } from "../../common/departments/ChooseEventDepartmentModal";
import { EntityTable, type EntityTableColumn } from "../../common/entity-table/EntityTable";
import { useDisplayDateWithTime } from "../../common/formatting/useDisplayDateWithTime";
import { urls } from "../../urls";
import { procurementEntryHref } from "../sourcing-event/procurementEntryHref";
import { LinkNeedDialog } from "./LinkNeedDialog";
import { MarketDialogueStatusTag } from "./MarketDialogueStatusTag";
import { useNoticeTypeLabel } from "./marketDialogueLabels";
import { useCreateMarketDialogue } from "../../common/market-dialogues/useCreateMarketDialogue";
import { useRunsPublicProcurements } from "../../common/organization/useRunsPublicProcurements";
import { useSub } from "../../common/subscription/useSub";

export const MarketDialoguesList = () => {
  const { t } = useTranslation();
  const runsPublicProcurements = useRunsPublicProcurements();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useListMarketDialoguesQuery({});
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [linkingNeedTo, setLinkingNeedTo] = useState<{ id: string; title: string } | null>(null);
  const noticeTypeLabel = useNoticeTypeLabel();
  const displayDateWithTime = useDisplayDateWithTime();
  const create = useCreateMarketDialogue((id) => navigate(urls.app.marketDialogues.view.go(id)));
  const dialogues = data?.items ?? [];
  const { hasNeedsAccess: showsNeeds } = useSub();

  const columns: EntityTableColumn[] = [
    { key: "title", label: t("Title"), width: "320px", alwaysShown: true },
    { key: "status", label: t("Status") },
    { key: "answers", label: t("Answers received") },
    { key: "answerDeadline", label: t("Answer deadline") },
    { key: "reach", label: t("Reaches the market through") },
    { key: "need", label: t("Need") },
    { key: "procurements", label: t("Procurements") },
  ]
    .filter((column) => showsNeeds || column.key !== "need")
    .map((column) => ({ ...column, hidden: hiddenColumns.includes(column.key) }));

  const rows = dialogues.map(({ need, procurements, ...dialogue }) => [
    <Button
      key="title"
      variant="link"
      color="smText"
      fontWeight="semibold"
      textAlign="left"
      whiteSpace="normal"
      onClick={() => navigate(urls.app.marketDialogues.view.go(dialogue.id))}
    >
      {dialogue.title.trim() || t("Untitled")}
    </Button>,
    <MarketDialogueStatusTag
      key="status"
      status={dialogue.status}
      announced={(dialogue.reachedThrough ?? []).includes(MarketDialogueReach.Doffin)}
      size="sm"
    />,
    <Text key="answers">{dialogue.answersReceived}</Text>,
    <Text key="answerDeadline" color="smMuted">
      {dialogue.answerDeadline ? displayDateWithTime(dialogue.answerDeadline) : "-"}
    </Text>,
    <Box key="reach">
      {(dialogue.reachedThrough ?? []).includes(MarketDialogueReach.Doffin) && <Text>{t("Announced on Doffin")}</Text>}
      {(dialogue.reachedThrough ?? []).includes(MarketDialogueReach.Invitation) && <Text>{t("Invited suppliers")}</Text>}
      {dialogue.noticeSubType && (
        <Text fontSize="sm" color="smMuted">
          {noticeTypeLabel(dialogue.noticeSubType)}
        </Text>
      )}
    </Box>,
    need ? (
      <Button
        key="need"
        variant="link"
        color="smPrimary"
        textAlign="left"
        whiteSpace="normal"
        onClick={() => navigate(urls.app.needs.view.go(need.id))}
      >
        {need.title?.trim() || t("Untitled")}
      </Button>
    ) : dialogue.canLinkNeed ? (
      <Button
        key="need"
        variant="link"
        size="sm"
        color="smPrimary"
        onClick={() => setLinkingNeedTo({ id: dialogue.id, title: dialogue.title })}
      >
        {t("Link to a need")}
      </Button>
    ) : (
      <Text key="need" color="smMuted">
        -
      </Text>
    ),
    procurements && procurements.length > 0 ? (
      <Stack key="procurements" spacing="1" align="flex-start">
        {procurements.map((procurement) => (
          <Button
            key={procurement.id}
            variant="link"
            color="smPrimary"
            textAlign="left"
            whiteSpace="normal"
            onClick={() => navigate(procurementEntryHref(procurement.status, procurement.id))}
          >
            {procurement.title.trim() || t("Untitled")}
          </Button>
        ))}
      </Stack>
    ) : (
      <Text key="procurements" color="smMuted">
        -
      </Text>
    ),
  ].filter((cell) => showsNeeds || cell.key !== "need"));

  return (
    <Box>
      <ListPageHeader
        heading={t("Market dialogues")}
        headingColor="smText"
        mb="2"
        actions={
          create.canCreate && (
            <Button colorScheme="teal" leftIcon={<Icon as={FiPlus} />} onClick={create.start} isLoading={create.isCreating}>
              {t("New market dialogue")}
            </Button>
          )
        }
      />
      <Text color="smMuted" mb="6" maxW="3xl">
        {runsPublicProcurements
          ? t(
              "Put draft tender documents and questions in front of suppliers before the competition. The dialogue is announced on Doffin as an indicative prior information notice.",
            )
          : t("Put draft tender documents and questions in front of the suppliers you invite before the competition.")}
      </Text>
      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" p="4">
        <EntityTable
          emptyText={t("No market dialogues yet.")}
          noMatchText={t("No market dialogues yet.")}
          loadError={isError ? { message: t("Could not load the market dialogues."), onRetry: () => void refetch() } : undefined}
          columns={columns}
          rows={rows}
          isLoading={isLoading}
          limit={Math.max(dialogues.length, 5)}
          skip={0}
          setSkip={() => undefined}
          onShowColumn={(key) => setHiddenColumns((hidden) => hidden.filter((k) => k !== key))}
          onHideColumn={(key) => setHiddenColumns((hidden) => [...hidden, key])}
          onShowAllColumns={() => setHiddenColumns([])}
          onResetColumns={() => setHiddenColumns([])}
          hasColumnChanges={hiddenColumns.length > 0}
        />
      </Box>
      {linkingNeedTo && (
        <LinkNeedDialog
          marketDialogueId={linkingNeedTo.id}
          marketDialogueTitle={linkingNeedTo.title}
          onClose={() => setLinkingNeedTo(null)}
        />
      )}
      {create.isChoosingDepartment && (
        <ChooseEventDepartmentModal
          isOpen
          departments={create.departments}
          isCreating={create.isCreating}
          onCancel={create.cancelChoosingDepartment}
          onConfirm={create.confirmDepartment}
          wording={{
            title: t("New market dialogue"),
            question: t("Which department should the market dialogue belong to?"),
            noDepartment: t("You cannot create market dialogues in any department in this organization."),
          }}
        />
      )}
    </Box>
  );
};
